import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { EventRecord } from '@iot-dashboard/shared';
import { useEvents } from '../../api/hooks';
import { Panel } from '../../components/Panel';
import { SeverityBadge } from '../../components/SeverityBadge';
import { ErrorState, Loading } from '../../components/States';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { formatDateTime } from '../../lib/format';

const TYPES = ['', 'alert', 'webhook', 'sync', 'audit', 'system'] as const;

export function EventsPage() {
  const { tenantId, base } = useTenantContext();
  const [type, setType] = useState<string>('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const events = useEvents(tenantId, 200, type || undefined);

  const rowOf = (e: EventRecord) => (
    <>
      <tr key={e.eventId} className="clickable" onClick={() => setExpanded((x) => (x === e.eventId ? null : e.eventId))}>
        <td className="nowrap">{formatDateTime(e.ts)}</td>
        <td><span className="badge badge--neutral">{e.type}</span></td>
        <td>{e.severity ? <SeverityBadge severity={e.severity} /> : null}</td>
        <td>{e.title}</td>
        <td>{e.deviceId ? <Link to={`${base}/sensors/${encodeURIComponent(e.deviceId)}`} onClick={(ev) => ev.stopPropagation()}>{e.deviceName ?? e.deviceId}</Link> : <span className="muted">{e.deviceName ?? '—'}</span>}</td>
        <td className="small">{e.actor ?? e.source}</td>
      </tr>
      {expanded === e.eventId ? (
        <tr key={`${e.eventId}-payload`}>
          <td colSpan={6}>
            <pre className="code-block">{JSON.stringify({ eventId: e.eventId, source: e.source, payload: e.payload ?? null }, null, 2)}</pre>
          </td>
        </tr>
      ) : null}
    </>
  );

  return (
    <>
      <PageHeader title="Event log" subtitle="Alerts, webhooks, sync runs and administrative changes for this tenant." />
      <Panel
        flush
        title={<div className="filters">
          <select className="select" value={type} onChange={(e) => setType(e.target.value)} aria-label="Event type">
            {TYPES.map((t) => <option key={t} value={t}>{t ? t[0].toUpperCase() + t.slice(1) : 'All event types'}</option>)}
          </select>
        </div>}
        actions={<span className="small muted">{events.data?.length ?? 0} events</span>}
      >
        {events.isPending ? <Loading /> : events.isError ? <ErrorState error={events.error} retry={() => events.refetch()} /> : (
          <div className={`table-wrap${events.isFetching ? ' refetching' : ''}`}>
            <table className="table table--dense">
              <thead><tr><th>Time</th><th>Type</th><th>Severity</th><th>Event</th><th>Sensor</th><th>Actor / source</th></tr></thead>
              <tbody>
                {events.data.length === 0 ? <tr><td colSpan={6}><div className="empty">No events</div></td></tr> : events.data.map(rowOf)}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
